import { useEffect, useRef, useState } from "react"
import { useSelector, useStore } from "react-redux"
import { RootState } from "../../../../store"
import { NoticiasAdapters } from "../adapters/noticias.adapters"

/**
 * Servei de continguts del WS d'Activitats (adminactivitats).
 *
 * És un servei EXTERN a la API del backoffice, per això NO fa servir `useAxios`
 * (la URL base i el format de paràmetres són diferents): es crida amb `fetch` i
 * la resposta es normalitza amb `NoticiasAdapters`. El token de sessió del
 * backoffice s'envia al WS mitjançant la capçalera `Authorization`.
 *
 * URL: {REACT_APP_WEB_SERVICE_ACTIVIDADES}/integraWeb.php?accion=...
 *
 * Además manda el CIF de la empresa en la cabecera `X-Empresa-Cif`. Lo rellena
 * `EmpresaDetalle` en redux (`state.empresa.cif`) al cargar el detalle, que además
 * no monta las pestañas hasta que el CIF está en el store (en un montaje React lanza
 * los efectos de los hijos antes que los del padre, así que sin ese gate la carga
 * inicial de una pestaña saldría sin cabecera).
 *
 * Aquí se lee del store en el MOMENTO de la petición y no con `useSelector` en el
 * cuerpo del hook: no hace falta repintar el componente cuando el CIF cambia, solo
 * consultarlo al pedir datos.
 *
 * Se manda solo si hay CIF, para no enviar la cabecera vacía en las llamadas que se
 * hacen fuera del contexto de una empresa.
 */
export const useIntegraWeb = (adapter: any, options?: any) => {

    const { auth } = useSelector((state: RootState) => state.auth)

    const { language } = useSelector((state: RootState) => state.language)

    // `getState()` en lugar de `useSelector` para leer el CIF: no hace falta
    // repintar el componente cuando cambia, solo consultarlo al pedir datos.
    const store = useStore()

    const token = auth.token

    // Por defecto el WS se llama por GET (accion + parámetros en la query). Para
    // acciones de guardado (p. ej. inscribir_personas_actividad) se pasa
    // { method: 'post' } y los parámetros viajan en el cuerpo JSON
    // (file_get_contents('php://input') en el WS).
    const metodo = options?.method || 'get'

    // Mapea el idioma actual de la app ('CA' | 'ES') a una etiqueta Accept-Language
    // para que el WS responda en el mismo idioma que la interfaz.
    const idiomaAceptado = () => {

        const etiquetas: any = {
            CA: 'ca',
            ES: 'es',
        }

        return etiquetas[language] || language || 'ca'
    }

    const mounted = useRef(true)

    const [state, setState] = useState<any>({ data: null, loading: null, error: null, status: null })

    useEffect(() => {

        return () => {

            mounted.current = false
        }
    }, [])

    const setStateIfMounted = (nuevoEstado: any) => {

        if (mounted.current) setState(nuevoEstado)
    }

    const fetchDatos = async (values?: any) => {

        const base = process.env.REACT_APP_WEB_SERVICE_ACTIVIDADES

        if (!base) {

            setStateIfMounted({ data: null, loading: false, error: `REACT_APP_WEB_SERVICE_ACTIVIDADES no definida`, status: null })
            return
        }

        // Se lee aquí y no en el cuerpo del hook para que la petición salga con el CIF
        // aunque se dispare en el mismo montaje que la carga del detalle de la empresa.
        const cifActual = (store.getState() as RootState).empresa?.cif || ''

        const params = adapter.send.adapter(values || {})

        const query = Object.entries(params)
            .filter(([, value]) => value !== '' && value != null)
            .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
            .join('&')

        const url = `${base}/integraWeb.php` + (metodo === 'get' ? `?${query}` : '')

        setStateIfMounted({ data: null, loading: true, error: null, status: null })

        try {

            const response = await fetch(url, {
                method: metodo,
                headers: {
                    // Token de sessió del backoffice buscat pel WS d'Activitats
                    Authorization: token || '',
                    // Idioma actual de la interfície, perquè el WS respongui en el mateix idioma
                    'Accept-Language': idiomaAceptado(),
                    // CIF de l'empresa que s'està consultant. El WS el llegeix amb
                    // `getallheaders()['X-Empresa-Cif']` (o `$_SERVER['HTTP_X_EMPRESA_CIF']`).
                    // El preflight no és problema: `webServiceAdmin/conf.php` fa eco del
                    // `Access-Control-Allow-Headers` que demani el navegador.
                    ...(cifActual ? { 'X-Empresa-Cif': cifActual } : {}),
                    // En POST el WS lee el cuerpo JSON (php://input)
                    ...(metodo !== 'get' ? { 'Content-Type': 'application/json' } : {}),
                },
                body: metodo !== 'get' ? JSON.stringify(params) : undefined,
            })

            const json = await response.json()

            if (!response.ok) {

                setStateIfMounted({
                    data: null,
                    loading: false,
                    error: json?.message || json?.error || `HTTP ${response.status}`,
                    status: response.status,
                })
                return
            }

            const adapted = adapter.receive.adapter(json)

            setStateIfMounted({
                data: adapted,
                loading: false,
                error: null,
                status: response.status,
            })
        }
        catch (error) {

            setStateIfMounted({ data: null, loading: false, error, status: null })
        }
    }

    const resetDatos = () => {

        setState({ data: null, loading: null, error: null, status: null })
    }

    return [fetchDatos, state, resetDatos] as const
}

const NoticiasServices = {

    GET_NOTICIAS: function () {

        return useIntegraWeb(NoticiasAdapters.GET_NOTICIAS)
    },

    GET_NOTICIA: function () {

        return useIntegraWeb(NoticiasAdapters.GET_NOTICIA)
    },
}

export {
    NoticiasServices
}